import { connect } from 'react-redux'
import { withReducer as dynamicWithReducer } from 'react-redux-dynamic-reducer'
import hoistNonReactStatics from 'hoist-non-react-statics'
import { namespaced } from 'redux-subspace' // 由 react-redux-dynamic-reducer 引入，不再写入依赖

const EXTRA_ROOT_STATE_REF = Symbol(
  'next/al-ex/client-export/withRedux#EXTRA_ROOT_STATE_REF'
)
const EXTRA_LAST_NAMESPACE = Symbol(
  'next/al-ex/client-export/withRedux#EXTRA_LAST_NAMESPACE'
)
const EXTRA_MAP_STATE_RESULT_REF = Symbol(
  'next/al-ex/client-export/withRedux#EXTRA_MAP_STATE_RESULT_REF'
)

// 统一 redux 注入
// withRedux((state) => {}) // 其中，state 为 rootState为pageReducer，对被包装组件注入 remap结果+dispatch
// withRedux(namespace, reducer, (state, rootState) => {}) // 其中，state为pageReducer，对被包装组件注入 remap结果+dispatch
export function withRedux(namespace, namespaceReducer, mapExtraState) {
  if (arguments.length === 0) {
    namespace = (state) => state
  }

  if (typeof namespace === 'function') {
    return connect(function (state) {
      return !state[EXTRA_ROOT_STATE_REF]
        ? // 无父级 withRedux(namespace...) 时，state 为 rootState
          state
        : // 否则为 rootState + pageReducer
          namespace({
            ...state[EXTRA_ROOT_STATE_REF],
            ...(state[EXTRA_ROOT_STATE_REF] &&
              state[EXTRA_ROOT_STATE_REF][state[EXTRA_LAST_NAMESPACE]]),
          })
    })
  }
  if (namespace.indexOf('/') !== -1) {
    throw new Error(`namespace(${namespace}) 不能包含 /`)
  }
  return function (Component) {
    // 1.x 行为诡异，见 test/integration/al-ex-extension-with-redux/pages/nested-passthrough/index.jsx 说明
    // 2.x
    //  withRedux(namespace, reducer, (state, rootState) => {}) state为pageReducer，rootState为根state，被包装组件注入 mapResult 和 dispatch
    //  withRedux(state => {})  state为rootState+pageReducer，被包装组件注入 mapResult 和 dispatch

    let TargetComponent = Component

    // 完成业务 mapState 推导
    TargetComponent = connect((argState) => {
      // 最终提供给被包装组件的props，为 mapState 的结果
      return argState[EXTRA_MAP_STATE_RESULT_REF]
    })(TargetComponent)

    // 注入子 reducer
    TargetComponent = withNamespacedReducer(namespaceReducer, namespace, {
      // 设置为 false 后，实质上 局部 actions 与 全局 actions 在同一级别，reducer 中的 action.type 需全局唯一
      namespaceActions: false,
      mapExtraState: (state, rootState) => {
        // 注意
        // 性能问题
        // 每个使用 withRedux 的子组件实例话，都会导致父级至更上一层父级的，重新 mapState 计算一次，但 render 仅一次

        const mapResult = mapExtraState(rootState[namespace], rootState)
        return {
          // 满足 dynamicWithReducer API，不断的将注入reducer到当前层级的store向下传递并新建
          ...state,
          // 传递引用，由下一层分离
          [EXTRA_ROOT_STATE_REF]: rootState,
          [EXTRA_LAST_NAMESPACE]: namespace,
          [EXTRA_MAP_STATE_RESULT_REF]: mapResult,
        }
      },
    })(TargetComponent)

    // getInitialProps 被重写过，需要保留 TargetComponent 上的版本
    const refGetInitialProps = TargetComponent.getInitialProps
    hoistNonReactStatics(TargetComponent, Component)
    TargetComponent.getInitialProps = refGetInitialProps

    return TargetComponent
  }
}

// 原始 withReducer 未支持 SSR，getInitialProps 时 hoc 注入的 reducer 未生效（实例化时才有效）
// 提前至 getInitialProps 时生效
// 对于非页面的普通 Component，尽管会额外的包装 getInitialProps，但实际不执行，无副作用
function withNamespacedReducer(bundledReducer, identifier, options) {
  if (identifier.indexOf('/') !== -1) {
    throw new Error(`identifier(${identifier}) 不能包含 /`)
  }
  const withFunc = dynamicWithReducer(bundledReducer, identifier, options)

  function injectBehavior(store) {
    // 参考 https://github.com/ioof-holdings/redux-dynamic-reducer/blob/v2.0.2/packages/react-redux-dynamic-reducer/src/withReducer.js
    // 完整实现逻辑
    let namespace
    let reducer = bundledReducer
    if (options.namespaceActions) {
      namespace = identifier
      reducer = namespaced(namespace)(bundledReducer)
    }

    const storeNamespace = store.namespace
    const state = {
      identifier: storeNamespace
        ? `${storeNamespace}/${identifier}`
        : identifier,
      namespacer: namespaced(store.namespace),
    }
    store.attachReducers({ [state.identifier]: state.namespacer(reducer) })
  }

  return function wrap(Component) {
    const ref = Component.getInitialProps
    Component.getInitialProps = async function (appCtx) {
      injectBehavior(appCtx.store || appCtx.ctx.store)
      if (ref) {
        return await ref.call(this, appCtx)
      } else {
        return {}
      }
    }

    return withFunc(Component)
  }
}
